import { Route, Routes, useNavigate } from "react-router-dom"; // <-- No BrowserRouter here!

import { AuthProvider, useAuth, useAuthUpdate } from "context/AuthContext";
import { NotificationProvider } from "context/NotificationContext";
import { TooltipProvider } from "context/TooltipProvider";
import { jwtDecode } from "jwt-decode";
import AppealList from "pages/Appeal/AppealList";
import AppealRequest from "pages/Appeal/AppealRequest";
import AppealDetail from "pages/Appeal/AppelDetail";
import CivilCaseAppealList from "pages/CivilCaseAppeal/CivilCaseAppealList";
import CivilCaseAppealDetail from "pages/CivilCaseAppeal/CivilCaseAppelDetail";
import CivilCaseComplaintDetail from "pages/CivilCaseComplaint/CivilCaseComplaintDetail";
import CivilCaseComplaintList from "pages/CivilCaseComplaint/CivilCaseComplaintList";
import CivilCaseComplaintRegister from "pages/CivilCaseComplaint/CivilCaseComplaintRegister";
import ComplaintDetail from "pages/Complaint/ComplaintDetail";
import ComplaintList from "pages/Complaint/ComplaintList";
import ComplaintRegister from "pages/Complaint/ComplaintRegister";
import DashboardPage from "pages/Dashboard/DashboardPage";
import FeedbackList from "pages/Feedback/FeedbackList";
import UserList from "pages/User/UserList";
import { useEffect, useState } from "react";
import { adminOrgStructure } from "services/orgService";
import { LoginAccount, Roles } from "types";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import { hasRole } from "utils/helper";

export interface JwtPayload {
  exp?: number; // Expiration time in seconds
  iat?: number; // Issued at
  sub?: string; // Subject (user id, etc.)
  [key: string]: any; // Any additional claims
}

export function isValidJwtFormat(token: string): boolean {
  if (!token || typeof token !== "string") return false;

  const parts = token.split(".");
  if (parts.length !== 3) return false;

  // Check if all parts are non-empty
  return parts.every((part) => part.trim().length > 0);
}

export function getTokenPayloadSafely(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token || !isValidJwtFormat(token)) {
    // console.warn("Invalid or missing token format");
    return null;
  }

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    // console.error("Error decoding token:", error);
    return null;
  }
}

export function decodeAccessToken(
  token: string | null | undefined,
): JwtPayload | null {
  if (!token) return null;

  try {
    return getTokenPayloadSafely(token);
  } catch (err) {
    // console.error("Failed to decode access token:", err);

    // Fallback to manual decoding
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) return null;

      const base64Url = tokenParts[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
          .join(""),
      );

      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (fallbackErr) {
      // console.error(
      //   "Failed to decode access token with fallback method:",
      //   fallbackErr
      // );
      return null;
    }
  }
}

function AppContent() {
  const { setLoginData } = useAuth();
  const navigate = useNavigate();
  const contextData = useAuth();
  const updateAuthContext = useAuthUpdate();
  // const { user } = useAuth();

  const [isMember, setIsMember] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  const fetchStructure = (adminId: number, orgId: number) => {
    return adminOrgStructure(adminId, orgId);
  };

  useEffect(() => {
    // console.log("handle message effect");
    async function handleMessage(event: MessageEvent) {
      // console.log(
      //   "token:",
      //   event.data?.token || event?.data?.data?.access_token
      // );
      // console.log("data", event.data);
      // console.log("event", event);
      const token =
        event?.data?.data?.access_token ||
        event.data?.token ||
        event.data?.access;
      if (
        event.data?.type === "SET_SESSION_DATA_FOR_PROSECUTION" ||
        event.data?.type === "SET_TOKEN"
      ) {
        // console.log("token", token);

        if (token && isValidJwtFormat(token)) {
          // console.log("Token received and stored");
          const payload = getTokenPayloadSafely(token);
          // console.log("payload", payload);

          let structure = null;
          if (payload?.admin_unit || payload?.organization?.admin_unit) {
            structure = await fetchStructure(
              (
                payload?.admin_unit || payload?.organization?.admin_unit
              ).toString(),
              payload?.org_unit ? payload?.org_unit.toString() : +"",
            );
          }

          // console.log("structure", structure);

          sessionStorage.setItem(
            "access_token",
            event.data?.token || event?.data?.data?.access_token || token,
          );

          setLoginData({
            ...payload,
            access: payload?.access || payload?.access_token || token,
            refresh: payload?.refresh || payload?.refresh_token || token,

            structure,
          } as any);

          navigate(
            // hasRole(payload?.roles, [Roles.mobileAppAdmin])
            //   ? "user"
            //   : "complaint",
            "/",
            {
              replace: true,
            },
          );

          // console.log(event.data?.token, event?.data?.data?.access_token);

          // updateAuthContext({
          //   ...contextData,
          //   token: event.data?.token,
          //   loginData: {
          //     id: payload?.user_id?.toString() ?? "",
          //     user_id: payload?.user_id?.toString() ?? "",
          //     employee_id: payload?.employee_id?.toString() ?? "",
          //     admin_unit: payload?.admin_unit?.toString() ?? "",
          //     org_unit: payload?.org_unit?.toString() ?? "",
          //     user_type: payload?.user_type?.toString() ?? "external",
          //     access: event.data.token,
          //     roles: payload?.roles,
          //     full_name: payload?.full_name,
          //     username: payload?.username,
          //     permissions: payload?.permissions,
          //     access_token: "",
          //     refresh: "",
          //     refresh_token: "",
          //     type: "Main",
          //     police_station_name: null,
          //     is_superuser: false,
          //   },
          // });

          // setIsMember(
          //   payload?.role === "Member" || payload?.is_police ? true : false,
          // );
          // console.log("Token payload:", payload);
          // if (payload?.user_type === "internal") {
          //   setInternalUserType(true);
          // }
          // setTokenLoaded(true);
        } else {
          // console.error("Invalid token received in SET_TOKEN message");
          setTokenLoaded(true);
        }
      }
    }

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const hasToken =
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("access_token");

    if (hasToken) {
      const payload = getTokenPayloadSafely(hasToken);

      setIsAuthenticated(true);
      updateAuthContext({
        ...contextData,
        token: hasToken,
        loginData: payload as LoginAccount,
      });
    }
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    navigate("/complaint"); // Redirect to main page after login
  };

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    setIsAuthenticated(false);
    updateAuthContext({
      ...contextData,
      token: "",
      loginData: {} as LoginAccount,
    });
  };

  // if (!isAuthenticated) {
  //   return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  // }

  const appRoutes = (
    <Routes>
      {/* <Route path="/incident-list" element={<IncidentList />} /> */}
      {/* <Route path="/incident-register" element={<IncidentRegister />} /> */}
      <Route path="/" element={<DashboardPage />} />
      <Route path="/complaint" element={<ComplaintList />} />
      <Route path="/complaint/:complaintId" element={<ComplaintDetail />} />
      <Route path="/complaint/register" element={<ComplaintRegister />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      {/* CIVIL CASE */}
      <Route
        path="/civil-case-complaint"
        element={<CivilCaseComplaintList />}
      />
      <Route
        path="/civil-case-complaint/:complaintId"
        element={<CivilCaseComplaintDetail />}
      />
      <Route
        path="/civil-case-complaint/register"
        element={<CivilCaseComplaintRegister />}
      />

      <Route path="/appeal" element={<AppealList />} />
      <Route path="/appeal/request" element={<AppealRequest />} />
      <Route path="/appeal/:appealId" element={<AppealDetail />} />

      <Route path="/civil-appeal" element={<CivilCaseAppealList />} />
      <Route
        path="/civil-appeal/:appealId"
        element={<CivilCaseAppealDetail />}
      />

      <Route path="/user" element={<UserList />} />
      <Route path="/feedback" element={<FeedbackList />} />
    </Routes>
  );

  return (
    <TooltipProvider>
      <div className="h-screen bg-gray-100">
        <div className="h-screen flex bg-gray-100">
          {/* Fixed Sidebar */}
          <div className="hidden md:block">
            <Sidebar handleLogout={handleLogout} />
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="fixed inset-0 z-40 md:hidden">
              <div
                className="fixed inset-0 bg-gray-600 bg-opacity-75"
                onClick={() => setSidebarOpen(false)}
              ></div>
              <div className="fixed inset-y-0 left-0 z-50 w-64">
                <Sidebar />
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Fixed Header */}
            {!isMember && <Header isInternalUser={true} />}

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto p-4 md:p-6">
              {/* Mobile Menu Button */}
              <div className="md:hidden mb-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="mobile-menu-btn"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
              {appRoutes}
            </main>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}
